import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, XCircle, Info, RefreshCw, Smartphone, Loader2, Video, Zap } from "lucide-react";

// Carte « AdMob — état sur les appareils » (Admin › Publicités) : constats en français
// (« pourquoi rien ne s'affiche »), résumé par plateforme et journal des étapes que
// l'app native remonte (plugin, SDK, consentement UMP, chargement, affichage).
const STEP_LABELS = {
  plugin_missing: "Plugin AdMob absent du build",
  unit_missing: "Ad Unit ID manquant",
  init_ok: "SDK Google démarré",
  init_failed: "SDK Google en échec",
  consent: "Consentement UMP",
  load_ok: "Annonce chargée",
  load_failed: "Chargement refusé par Google",
  slot_stuck: "Cadre visible sans annonce",
  probe_ok: "Sonde : vue native bien dans son cadre",
  probe_fail: "Sonde : vue native HORS de son cadre (réparée)",
  preload_ok: "Réservoir rempli (annonce prête en mémoire)",
  preload_failed: "Réservoir : chargement refusé par Google",
  shown: "Annonce affichée",
  show_failed: "Affichage impossible",
  skipped_yalola: "Sauté (campagne Yalola, mode repli)",
};
const LEVEL = {
  ok: { Icon: CheckCircle2, color: "#059669", bg: "#ECFDF5" },
  warn: { Icon: AlertTriangle, color: "#B45309", bg: "#FFFBEB" },
  error: { Icon: XCircle, color: "#B91C1C", bg: "#FEF2F2" },
  info: { Icon: Info, color: "#1D4ED8", bg: "#EFF6FF" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const PlatformSummary = ({ pf, p }) => {
  const last = p?.last_event;
  const v = p?.video_7d || {};
  const w = p?.warm_7d || {};
  const videoLine = v.known
    ? `${v.with_video} / ${v.known} annonce(s) native(s) avec vidéo (${v.percent} %)`
    : (p?.shown_7d ? "part inconnue (builds sans remontée « vidéo »)" : "aucune annonce affichée");
  // Lot 634 : part des annonces posées SANS attente réseau (réservoir) — remontée par les builds ≥ 2.2 (23).
  const warmLine = w.known
    ? `${w.warm} / ${w.known} annonce(s) posée(s) sans attente réseau (${w.percent} %)`
    : "aucune remontée (builds antérieurs à la 2.2)";
  return (
    <div className="rounded-xl p-3 text-xs" style={{ background: "var(--yalola-surface-alt)" }} data-testid={`admob-diag-${pf}`} data-enabled={p?.enabled ? "1" : "0"}>
      <div className="flex items-center gap-1.5 font-bold mb-1"><Smartphone size={13} /> {pf === "android" ? "Android" : "iOS"} · {p?.enabled ? "activé" : "désactivé"}</div>
      <div style={{ color: "var(--yalola-text-muted)" }}>Natif Reels : {p?.reels_native_unit_set ? "renseigné" : "vide"} · Natif Fil &amp; Profil : {p?.feed_native_unit_set ? "renseigné" : "vide"} · tous les {p?.frequency || 8} reels · tous les {p?.feed_frequency || 6} posts</div>
      <div style={{ color: "var(--yalola-text-muted)" }}>Dernier signal : {last ? `${STEP_LABELS[last.step] || last.step}${last.placement ? ` (${last.placement})` : ""} · ${fmt(last.created_at)}` : "aucun appareil pour l'instant"}</div>
      <div style={{ color: "var(--yalola-text-muted)" }}>7 jours : {p?.shown_7d ?? 0} annonce(s) affichée(s) · {p?.devices_7d ?? 0} appareil(s)</div>
      <div className="flex items-center gap-1" style={{ color: v.known && !v.with_video ? "#B45309" : "var(--yalola-text-muted)" }} data-testid={`admob-diag-${pf}-video`} data-known={v.known ?? 0} data-with-video={v.with_video ?? 0}>
        <Video size={12} /> Vidéo (7 j) : {videoLine}
      </div>
      <div className="flex items-center gap-1" style={{ color: w.known && w.percent < 50 ? "#B45309" : "var(--yalola-text-muted)" }} data-testid={`admob-diag-${pf}-warm`} data-known={w.known ?? 0} data-warm={w.warm ?? 0}>
        <Zap size={12} /> Réservoir (7 j) : {warmLine}
      </div>
    </div>
  );
};

export const AdmobDiagnosticsCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/admin/google-ads/diagnostics"); setData(d); }
    catch { setData(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  if (!data && loading) return <div className="yalola-card p-5 text-sm" data-testid="admob-diagnostics-loading"><Loader2 size={14} className="animate-spin inline mr-1.5" /> Lecture de l'état AdMob…</div>;
  if (!data) return null;
  const events = showAll ? data.events : data.events.slice(0, 8);
  return (
    <div className="yalola-card p-5" data-testid="admob-diagnostics-card" data-test-mode={data.test_mode ? "1" : "0"} data-reels-mode={data.reels_mode}>
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="font-bold font-display text-lg">AdMob — état sur les appareils</div>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            L'app native remonte chaque étape d'une annonce Google (plugin → SDK → consentement → chargement → affichage). Mode TEST : <b>{data.test_mode ? "activé" : "désactivé"}</b> · Reels : <b>{data.reels_mode === "fallback" ? "repli seul" : "en complément des campagnes Yalola"}</b> · campagnes Yalola en diffusion : <b>{data.active_yalola_campaigns}</b>.
            {" "}<Link to="/admin?tab=settings&section=finance&focus=settings-admob-reels-mode-section" className="underline" data-testid="admob-diag-settings-link">Réglages</Link>
          </p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="admob-diag-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
      </div>
      <div className="space-y-1.5 mt-3" data-testid="admob-diag-checks">
        {data.checks.map((c) => {
          const L = LEVEL[c.level] || LEVEL.info;
          return (
            <div key={c.key} className="flex items-start gap-2 rounded-xl px-3 py-2 text-xs" style={{ background: L.bg, color: "#1f2937" }} data-testid={`admob-check-${c.key}`} data-level={c.level}>
              <L.Icon size={14} style={{ color: L.color, flexShrink: 0, marginTop: 1 }} />
              <span>{c.text}</span>
            </div>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mt-3">
        <PlatformSummary pf="android" p={data.platforms?.android} />
        <PlatformSummary pf="ios" p={data.platforms?.ios} />
      </div>
      {data.events.length > 0 && (
        <div className="mt-3" data-testid="admob-diag-events">
          <div className="text-xs font-bold mb-1">Journal des appareils ({data.events.length})</div>
          <div className="divide-y text-xs" style={{ borderColor: "var(--yalola-border)" }}>
            {events.map((e) => (
              <div key={e.id} className="py-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`admob-event-${e.id}`} data-step={e.step} data-ok={e.ok ? "1" : "0"}>
                <span style={{ color: "var(--yalola-text-muted)" }}>{fmt(e.created_at)}</span>
                <span className="font-semibold">{e.platform === "android" ? "Android" : "iOS"}</span>
                <span className="rounded-full px-2 py-0.5" style={{ background: e.ok ? "#ECFDF5" : "#FEF2F2", color: e.ok ? "#065F46" : "#991B1B" }}>{STEP_LABELS[e.step] || e.step}</span>
                {e.detail && <span style={{ color: "var(--yalola-text-muted)" }}>{e.detail}</span>}
                {e.app_version && <span style={{ color: "var(--yalola-text-muted)" }}>· v{e.app_version}</span>}
                {e.test_mode && <span style={{ color: "var(--yalola-text-muted)" }}>· test</span>}
              </div>
            ))}
          </div>
          {data.events.length > 8 && (
            <button type="button" className="text-xs underline mt-1" onClick={() => setShowAll((v) => !v)} data-testid="admob-diag-toggle-all">{showAll ? "Réduire" : `Voir les ${data.events.length} derniers`}</button>
          )}
        </div>
      )}
    </div>
  );
};

export default AdmobDiagnosticsCard;
